<template>
  <b-container>
    <b-row>
      <b-col>
        <b-form-group label="Payment Frequency" label-for="frequency">
          <b-form-select
            id="frequency"
            v-model="localValue.frequency"
            :state="state('frequency')"
            :options="paymentFrequencies"
            aria-describedby="frequency-error"
          ></b-form-select>
          <b-form-invalid-feedback id="frequency-error">
            {{ errors.frequency }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <b-col cols="6" v-bind="attrs" v-on="on">
            <v-switch
              id="first-payment-date-toggle"
              v-model="execDateEnabled"
              label="Set Date of First Payment"
              dense
            ></v-switch>
          </b-col>
        </template>
        <span>Date The First Recurring Payment Is Made</span>
      </v-tooltip>
      <b-col v-if="execDateEnabled">
        <DateInput
          label="Date of First Payment"
          fieldName="executionDate"
          placeHolder="Enter the Date for the first Payment"
          required
          v-model="localValue.executionDate"
          :min="executionDateMinimum"
          :state="state('executionDate')"
          aria-describedby="executionDate-error"
        >
          <template v-slot:inputValidationErrors>
            <b-form-invalid-feedback
              id="executionDate-error"
              :forceShow="!state('executionDate')"
            >
              {{ errors.executionDate }}
            </b-form-invalid-feedback>
          </template>
        </DateInput>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <v-switch
          id="number-of-payments-toggle"
          v-model="disableNumberofPayments"
          label="Indefinite Payments"
          dense
        ></v-switch>
      </b-col>
      <b-col v-if="!disableNumberofPayments">
        <b-form-group label="Number of Payments" label-for="period">
          <b-form-input
            id="period"
            placeholder="Enter the number of Payments"
            required
            v-model="localValue.period"
            type="number"
            min="1"
            inputmode="numeric"
            pattern="[0-9]*"
            :state="state('period')"
            aria-describedby="period-error"
          />

          <b-form-invalid-feedback id="period-error">
            {{ errors.period }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row id="date__container">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <b-col v-bind="attrs" v-on="on">
            <DateInput
              label="Send Date"
              fieldName="sendDate"
              placeHolder="Enter the Date for the request to be sent to the Customer"
              required
              v-model="localValue.sendDate"
              :minFromToday="sendDateMinimumFromToday"
              :state="state('sendDate')"
              aria-describedby="sendDate-error"
            >
              <template v-slot:inputValidationErrors>
                <b-form-invalid-feedback
                  id="sendDate-error"
                  :forceShow="!state('sendDate')"
                >
                  {{ errors.sendDate }}
                </b-form-invalid-feedback>
              </template>
            </DateInput>
          </b-col>
        </template>
        <span>Send Date - When The Email Is Sent</span>
      </v-tooltip>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <b-col v-bind="attrs" v-on="on" id="expiry_container">
            <DateInput
              label="Expiry Date"
              fieldName="expiryDate"
              placeHolder="Enter the Expiration Date of the payment request"
              required
              v-model="localValue.expiryDate"
              :min="expiryDateMinimum"
              :state="state('expiryDate')"
              aria-describedby="expiryDate-error"
            >
              <template v-slot:inputValidationErrors>
                <b-form-invalid-feedback
                  id="expiryDate-error"
                  :forceShow="!state('expiryDate')"
                >
                  {{ errors.expiryDate }}
                </b-form-invalid-feedback>
              </template>
            </DateInput>
          </b-col>
        </template>
        <span>Expiry Date - When The Link In The Email Expires</span>
      </v-tooltip>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Payment Description" label-for="reason">
          <b-form-textarea
            id="reason"
            required
            v-model="localValue.reason"
            type="text"
            :state="state('reason')"
            aria-describedby="reason-error"
          />
          <b-form-invalid-feedback id="reason-error">
            {{ errors.reason }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import DateInput from "@/components/form-elements/elements/DateInput.vue";

export default {
  components: {
    DateInput
  },
  props: {
    value: {
      period: {
        type: Number,
        default: 1
      },
      frequency: {
        type: String,
        default: ""
      },
      sendDate: {
        type: Date,
        default: new Date()
      },
      executionDate: {
        type: Date,
        default: new Date()
      },
      expiryDate: {
        type: Date,
        default: new Date()
      },
      reason: {
        type: String,
        default: ""
      }
    },
    paymentFrequencies: {
      type: Array,
      default() {
        return [
          { value: "Daily", text: "Daily" },
          { value: "Weekly", text: "Weekly" },
          { value: "Fortnightly", text: "Fortnightly/Bi-Weekly" },
          { value: "Monthly", text: "Monthly" },
          { value: "BiMonthly", text: "Bi-Monthly" },
          { value: "Quarterly", text: "Quarterly" },
          { value: "Yearly", text: "Yearly" }
        ];
      }
    },
    errorMessages: {
      type: Object,
      required: false,
      default() {
        return {
          period: null,
          frequency: null,
          sendDate: null,
          executionDate: null,
          expiryDate: null,
          reason: null
        };
      }
    },
    executionDateEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    indefinitePeriod: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      sendDateMinimumFromToday: { year: 0, month: 0, day: 0 }
    };
  },
  watch: {
    localValue: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true
    },
    executionDate: {
      handler(newVal) {
        if (!this.execDateEnabled) return;

        if (this.localValue.expiryDate > this.executionDate) {
          this.localValue.expiryDate = this.executionDate;
        }
      }
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      }
    },
    execDateEnabled: {
      get() {
        return this.executionDateEnabled;
      },
      set(newVal) {
        this.$emit("update:executionDateEnabled", newVal);
      }
    },
    disableNumberofPayments: {
      get() {
        return this.indefinitePeriod;
      },
      set(newVal) {
        this.$emit("update:indefinitePeriod", newVal);
      }
    },
    expiryDateMinimum() {
      return this.localValue.sendDate;
    },
    executionDateMinimum() {
      return this.localValue.sendDate;
    },
    executionDate() {
      return this.localValue.executionDate;
    },
    errors() {
      return {
        period: this.getErrorMessage("period"),
        frequency: this.getErrorMessage("frequency"),
        sendDate: this.getErrorMessage("sendDate"),
        executionDate: this.getErrorMessage("executionDate"),
        expiryDate: this.getErrorMessage("expiryDate"),
        reason: this.getErrorMessage("reason")
      };
    }
  },
  methods: {
    getErrorMessage(name) {
      return !!this.errorMessages ? this.errorMessages[name] : null;
    },
    state(name) {
      return this.errors[name] == null ? null : !this.errors[name].length;
    }
  }
};
</script>

<style lang="scss" scoped></style>
